import * as Sentry from '@sentry/react';
import { handleCheckReCapture } from '~/utils/checkingSameVin';
import { themeActions } from '~/utils/config';
import { variablesBySessionStorage } from './storagesInstantlyVariables';

const safeSetSessionStorage = (key, value) => {
  try {
    variablesBySessionStorage('SET', key, value);
  } catch (error) {
    console.warn(`Failed to set session storage for ${key}:`, error);
    // Report to Sentry but don't crash the app
    if (Sentry) {
      Sentry.captureException(error, {
        tags: { error_type: 'session_storage_error' },
        extra: { key, value }
      });
    }
  }
};

// Safe vehicle action with error boundary
const safeShowVehicle = (showVehicle, vehicleData) => {
  try {
    // Use requestAnimationFrame to ensure DOM updates happen safely
    requestAnimationFrame(() => {
      try {
        showVehicle(vehicleData);
      } catch (error) {
        console.warn('Failed to show vehicle data:', error);
        if (Sentry) {
          Sentry.captureException(error, {
            tags: { error_type: 'vehicle_display_error' },
            extra: { vehicleData }
          });
        }
      }
    });
  } catch (error) {
    console.warn('Failed to schedule vehicle display:', error);
    // Fallback: try immediate execution
    try {
      showVehicle(vehicleData);
    } catch (fallbackError) {
      console.error('All vehicle display attempts failed:', fallbackError);
    }
  }
};

export const vehicleData = (
  data,
  sessionID,
  theme,
  detectedOutline,
  showVehicle
) => {
  // Input validation
  if (!data || typeof data !== 'object') {
    console.warn('Invalid vehicle data provided:', data);
    return;
  }

  const vehicle = data.vehicle || {};
  const cages = data.cages || {};

  // wrong variable name; misunderstaning the purpose of options (disclosures is a part of options not a full options); BUG options is assigning with a string not an object - remove the hot fix line later
  const inspectionOptions = data.options || {};
  
  //Safe cages handling
  try {
    if (cages && typeof detectedOutline === 'function') {
      detectedOutline(cages);
    }
  } catch (error) {
    console.warn('Failed to set detected outline:', error);
  }

  // Set default rejection counter to 2 if not specified
  const rejectedVinAllowed = inspectionOptions['rejected_vin_allowed'] || '2';
  safeSetSessionStorage('_REJECT_VIN_ALLOWED', rejectedVinAllowed);

  if (inspectionOptions['flow']) {
    safeSetSessionStorage('ALLOW_REPORT_VIEW', inspectionOptions['flow']);
  }

  if (inspectionOptions['skip_capture_report']) {
    safeSetSessionStorage('SKIP_REPORT', inspectionOptions['skip_capture_report']);
  }

  if (inspectionOptions['rejected_odom_allowed']) {
    safeSetSessionStorage('_REJECT_ODOM_ALLOWED', inspectionOptions['rejected_odom_allowed']);
  }

  // Enhanced vehicle info handling with validation
  if (vehicle.year && vehicle.make && vehicle.vin) {
    const yearMakeModel = `${vehicle.year} ${vehicle.make} ${vehicle?.model || ''}`.trim();
    const vinCode = `${vehicle.vin}`;
    
    const vehicleDisplayData = {
      yearMakeModel,
      vinCode,
      vehicleModel: vehicle?.model || '',
      _make: vehicle.make,
    };

    // Safe vehicle display with error handling
    if (typeof showVehicle === 'function') {
      safeShowVehicle(showVehicle, vehicleDisplayData);
    }
    
    //Safe theme-specific handling
    if (theme === themeActions.PAVE_DDC && sessionID) {
      try {
        handleCheckReCapture(sessionID)
          .then(({ data }) => {
            if (data && data.status === 'success') {
              const min_photos = data && data.min_photos;
              if (min_photos) {
                safeSetSessionStorage('UP', min_photos);
              }
            }
          })
          .catch((error) => {
            console.warn('Failed to check re-capture:', error);
            // Don't re-throw as this is not critical
          });
      } catch (error) {
        console.warn('Failed to initiate re-capture check:', error);
      }
    }
  } else {
    console.warn('Incomplete vehicle data:', { 
      hasYear: !!vehicle.year, 
      hasMake: !!vehicle.make, 
      hasVin: !!vehicle.vin 
    });
  }
};
